import uiComponentsSchema from './json-schemas/ui-components-schema.json';
import specSchema from './json-schemas/spec-schema.json';

// This file turns a raw page JSON into a clean "render tree" that the layout renderer can draw.
// It also does lightweight safety checks so only known components are used.

// Reads nested values like "form.name" from an object.
function getByPath(obj, path) {
  if (!path) return undefined;
  const normalizedPath = String(path)
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/\[['"]([^'"\]]+)['"]\]/g, '.$1')
    .replace(/^\./, '');

  return normalizedPath
    .split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function findTopLevelOperatorIndex(raw, operator) {
  let depth = 0;
  let inSingleQuote = false;

  for (let index = 0; index < raw.length; index += 1) {
    const char = raw[index];

    if (char === "'" && raw[index - 1] !== '\\') {
      inSingleQuote = !inSingleQuote;
      continue;
    }

    if (!inSingleQuote) {
      if (char === '(' || char === '{' || char === '[') depth += 1;
      if (char === ')' || char === '}' || char === ']') depth -= 1;

      if (depth === 0 && raw.slice(index, index + operator.length) === operator) {
        return index;
      }
    }
  }

  return -1;
}

function evaluateComparisonExpression(raw, context) {
  const operators = ['!==', '===', '>=', '<=', '!=', '==', '>', '<'];

  for (const operator of operators) {
    const index = findTopLevelOperatorIndex(raw, operator);
    if (index === -1) continue;

    const leftRaw = raw.slice(0, index).trim();
    const rightRaw = raw.slice(index + operator.length).trim();

    if (!leftRaw || !rightRaw) return null;

    const leftValue = evaluateExpression(leftRaw, context);
    const rightValue = evaluateExpression(rightRaw, context);

    switch (operator) {
      case '!==':
        return leftValue !== rightValue;
      case '===':
        return leftValue === rightValue;
      case '!=':
        return leftValue != rightValue; // eslint-disable-line eqeqeq
      case '==':
        return leftValue == rightValue; // eslint-disable-line eqeqeq
      case '>=':
        return Number(leftValue) >= Number(rightValue);
      case '<=':
        return Number(leftValue) <= Number(rightValue);
      case '>':
        return Number(leftValue) > Number(rightValue);
      case '<':
        return Number(leftValue) < Number(rightValue);
      default:
        return null;
    }
  }

  return null;
}

function parseLambdaExpression(raw) {
  if (typeof raw !== 'string') return null;

  const match = raw.trim().match(/^\(?\s*([A-Za-z_$][\w$]*)\s*\)?\s*=>\s*(.+)$/);
  if (!match) return null;

  return {
    paramName: match[1],
    body: match[2].trim()
  };
}

// Splits function-style strings like "$and(a, b, c)" into separate argument strings.
// It keeps commas inside nested expressions intact.
function splitArgs(raw) {
  return splitTopLevel(raw, ',');
}

function splitTopLevel(raw, separator) {
  const parts = [];
  let current = '';
  let depth = 0;
  let inSingleQuote = false;

  for (let index = 0; index < raw.length; index += 1) {
    const char = raw[index];

    if (char === "'" && raw[index - 1] !== '\\') {
      inSingleQuote = !inSingleQuote;
      current += char;
      continue;
    }

    if (!inSingleQuote) {
      if (char === '(' || char === '{' || char === '[') depth += 1;
      if (char === ')' || char === '}' || char === ']') depth -= 1;

      if (char === separator && depth === 0) {
        parts.push(current.trim());
        current = '';
        continue;
      }
    }

    current += char;
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
}

function unquoteString(raw) {
  if (typeof raw !== 'string') return raw;
  if (raw.startsWith("'") && raw.endsWith("'")) {
    return raw.slice(1, -1).replace(/\\'/g, "'");
  }
  return raw;
}

function deepClone(value) {
  if (Array.isArray(value)) {
    return value.map((item) => deepClone(item));
  }

  if (value && typeof value === 'object') {
    const copy = {};
    Object.entries(value).forEach(([key, inner]) => {
      copy[key] = deepClone(inner);
    });
    return copy;
  }

  return value;
}

function evaluateObjectLiteral(raw, context) {
  const content = raw.slice(1, -1).trim();
  if (!content) return {};

  const entries = splitTopLevel(content, ',');
  const parsed = {};

  entries.forEach((entry) => {
    const colonParts = splitTopLevel(entry, ':');
    if (colonParts.length < 2) return;

    const key = unquoteString(colonParts[0].trim());
    const expression = colonParts.slice(1).join(':').trim();
    parsed[key] = evaluateExpression(expression, context);
  });

  return parsed;
}

function evaluatePlusExpression(raw, context) {
  const parts = splitTopLevel(raw, '+');
  if (parts.length <= 1) return null;

  const values = parts.map((part) => evaluateExpression(part, context));
  const hasString = values.some((value) => typeof value === 'string');

  if (hasString) {
    return values.map((value) => (value == null ? '' : String(value))).join('');
  }

  return values.reduce((sum, value) => sum + Number(value || 0), 0);
}

// Tiny expression evaluator for a small set of schema expressions.
// This is intentionally minimal and only supports the expressions used in this test schema.
export function evaluateExpression(value, context = {}) {
  if (typeof value !== 'string') return value;

  const state = context.state || {};
  const args = context.args || {};
  const event = context.event || {};

  const trimmedValue = value.trim();

  if (trimmedValue.startsWith("'") && trimmedValue.endsWith("'")) {
    return unquoteString(trimmedValue);
  }

  if (trimmedValue === 'true') return true;
  if (trimmedValue === 'false') return false;
  if (trimmedValue === 'null') return null;

  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) {
    return Number(trimmedValue);
  }

  if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
    return evaluateObjectLiteral(trimmedValue, context);
  }

  if (trimmedValue.startsWith('$state.')) {
    return getByPath(state, trimmedValue.slice('$state.'.length));
  }

  if (trimmedValue.startsWith('$args.')) {
    return getByPath(args, trimmedValue.slice('$args.'.length));
  }

  if (trimmedValue.startsWith('$event.')) {
    return getByPath(event, trimmedValue.slice('$event.'.length));
  }

  if (trimmedValue === '$out') {
    return context.out ?? null;
  }

  if (trimmedValue.startsWith('$out.')) {
    return getByPath(context.out || {}, trimmedValue.slice('$out.'.length));
  }

  const scopedVars = context.vars || {};

  // $varName or $varName.path — $-prefixed access for loop-scoped variables (repeat context)
  if (trimmedValue.startsWith('$')) {
    const withoutDollar = trimmedValue.slice(1);
    if (Object.prototype.hasOwnProperty.call(scopedVars, withoutDollar)) {
      return scopedVars[withoutDollar];
    }
    if (withoutDollar.includes('.')) {
      const [head, ...rest] = withoutDollar.split('.');
      if (Object.prototype.hasOwnProperty.call(scopedVars, head)) {
        return getByPath(scopedVars[head], rest.join('.'));
      }
    }
  }

  // varName or varName.path — non-$-prefixed access (used in recursive sub-expressions like $filter lambdas)
  if (Object.prototype.hasOwnProperty.call(scopedVars, trimmedValue)) {
    return scopedVars[trimmedValue];
  }

  if (trimmedValue.includes('.')) {
    const [head, ...rest] = trimmedValue.split('.');
    if (Object.prototype.hasOwnProperty.call(scopedVars, head)) {
      return getByPath(scopedVars[head], rest.join('.'));
    }
  }

  if (trimmedValue.startsWith('$not(') && trimmedValue.endsWith(')')) {
    const inner = trimmedValue.slice(5, -1);
    return !Boolean(evaluateExpression(inner, context));
  }

  if (trimmedValue.startsWith('$trim(') && trimmedValue.endsWith(')')) {
    const inner = trimmedValue.slice(6, -1);
    return String(evaluateExpression(inner, context) ?? '').trim();
  }

  if (trimmedValue.startsWith('$and(') && trimmedValue.endsWith(')')) {
    const allArgs = splitArgs(trimmedValue.slice(5, -1));
    return allArgs.every((arg) => Boolean(evaluateExpression(arg, context)));
  }

  if (trimmedValue.startsWith('$or(') && trimmedValue.endsWith(')')) {
    const allArgs = splitArgs(trimmedValue.slice(4, -1));
    return allArgs.some((arg) => Boolean(evaluateExpression(arg, context)));
  }

  if (trimmedValue.startsWith('$match(') && trimmedValue.endsWith(')')) {
    const matchArgs = splitArgs(trimmedValue.slice(7, -1));
    if (matchArgs.length !== 2) return false;
    const source = String(evaluateExpression(matchArgs[0], context) ?? '');
    const pattern = String(unquoteString(matchArgs[1]));

    try {
      return new RegExp(pattern).test(source);
    } catch (_error) {
      return false;
    }
  }

  if (trimmedValue.startsWith('$append(') && trimmedValue.endsWith(')')) {
    const appendArgs = splitArgs(trimmedValue.slice(8, -1));
    if (appendArgs.length !== 2) return [];
    const target = evaluateExpression(appendArgs[0], context);
    const item = evaluateExpression(appendArgs[1], context);
    return [...(Array.isArray(target) ? target : []), item];
  }

  if (trimmedValue.startsWith('$filter(') && trimmedValue.endsWith(')')) {
    const filterArgs = splitArgs(trimmedValue.slice(8, -1));
    if (filterArgs.length !== 2) return [];

    const source = evaluateExpression(filterArgs[0], context);
    if (!Array.isArray(source)) return [];

    const lambda = parseLambdaExpression(filterArgs[1]);
    if (!lambda) return source;

    return source.filter((item) => {
      const result = evaluateExpression(lambda.body, {
        ...context,
        vars: {
          ...(context.vars || {}),
          [lambda.paramName]: item
        }
      });

      return Boolean(result);
    });
  }

  if (trimmedValue.startsWith('$length(') && trimmedValue.endsWith(')')) {
    const lenArgs = splitArgs(trimmedValue.slice(8, -1));
    const target = evaluateExpression(lenArgs[0], context);
    if (Array.isArray(target) || typeof target === 'string') {
      return target.length;
    }
    if (target && typeof target === 'object') {
      return Object.keys(target).length;
    }
    return 0;
  }

  if (trimmedValue.startsWith('$lower(') && trimmedValue.endsWith(')')) {
    const lowerArgs = splitArgs(trimmedValue.slice(7, -1));
    const target = evaluateExpression(lowerArgs[0], context);
    return String(target ?? '').toLowerCase();
  }

  if (trimmedValue.startsWith('$upper(') && trimmedValue.endsWith(')')) {
    const upperArgs = splitArgs(trimmedValue.slice(7, -1));
    const target = evaluateExpression(upperArgs[0], context);
    return String(target ?? '').toUpperCase();
  }

  if (trimmedValue.startsWith('$includes(') && trimmedValue.endsWith(')')) {
    const includesArgs = splitArgs(trimmedValue.slice(10, -1));
    if (includesArgs.length !== 2) return false;

    const haystack = evaluateExpression(includesArgs[0], context);
    const needle = evaluateExpression(includesArgs[1], context);

    if (Array.isArray(haystack)) {
      return haystack.some((item) => item === needle);
    }

    if (typeof haystack === 'string') {
      return haystack.includes(String(needle ?? ''));
    }

    return false;
  }

  if (trimmedValue.startsWith('$pluck(') && trimmedValue.endsWith(')')) {
    const pluckArgs = splitArgs(trimmedValue.slice(7, -1));
    if (pluckArgs.length !== 2) return [];

    const source = evaluateExpression(pluckArgs[0], context);
    const path = unquoteString(pluckArgs[1]);
    if (!Array.isArray(source) || typeof path !== 'string') return [];

    return source.map((item) => getByPath(item, path));
  }

  if (trimmedValue.startsWith('$max(') && trimmedValue.endsWith(')')) {
    const maxArgs = splitArgs(trimmedValue.slice(5, -1));
    if (maxArgs.length !== 1) return 0;

    const source = evaluateExpression(maxArgs[0], context);
    if (!Array.isArray(source) || source.length === 0) return 0;

    const numbers = source
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));

    if (numbers.length === 0) return 0;
    return Math.max(...numbers);
  }

  if (trimmedValue.startsWith('$now(') && trimmedValue.endsWith(')')) {
    const nowArgs = splitArgs(trimmedValue.slice(5, -1));
    const format = unquoteString(nowArgs[0] || 'YYYY-MM-DD');
    const now = new Date();

    if (format === 'YYYY-MM-DD') {
      return now.toISOString().slice(0, 10);
    }

    return now.toISOString();
  }

  const comparisonValue = evaluateComparisonExpression(trimmedValue, context);
  if (comparisonValue !== null) return comparisonValue;

  const plusValue = evaluatePlusExpression(trimmedValue, context);
  if (plusValue !== null) return plusValue;

  return value;
}

// Walks through props and replaces dynamic values (like "$state.form.name") with real values.
export function resolveDynamicValues(value, state, contextOverrides = {}) {
  const context = {
    state,
    args: {},
    event: {},
    ...contextOverrides
  };

  if (Array.isArray(value)) {
    return value.map((item) => resolveDynamicValues(item, state, contextOverrides));
  }

  if (value && typeof value === 'object') {
    const next = {};
    Object.entries(value).forEach(([key, inner]) => {
      next[key] = resolveDynamicValues(inner, state, contextOverrides);
    });
    return next;
  }

  if (typeof value === 'string' && value.startsWith('$')) {
    return evaluateExpression(value, context);
  }

  return value;
}

function recomputeComputedState(stateSpec = {}, state = {}) {
  const nextState = { ...state };

  Object.entries(stateSpec).forEach(([key, node]) => {
    if (node?.type === 'computed') {
      const expression = node?.expression || node?.compute;
      if (typeof expression === 'string') {
        nextState[key] = evaluateExpression(expression, { state: nextState });
      }
    }
  });

  return nextState;
}

function mergeNestedObjects(previousValue, nextValue) {
  if (nextValue === undefined) return previousValue;

  const isPrevObject = previousValue && typeof previousValue === 'object' && !Array.isArray(previousValue);
  const isNextObject = nextValue && typeof nextValue === 'object' && !Array.isArray(nextValue);

  if (!isPrevObject || !isNextObject) {
    return nextValue;
  }

  const merged = { ...previousValue };
  Object.entries(nextValue).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    merged[key] = mergeNestedObjects(previousValue?.[key], value);
  });

  return merged;
}

function mergeStateUpdates(baseState = {}, updates = {}, stateSpec = {}) {
  const merged = { ...baseState };

  Object.entries(updates || {}).forEach(([key, updateValue]) => {
    const nodeType = stateSpec?.[key]?.type;
    if (nodeType === 'object') {
      merged[key] = mergeNestedObjects(baseState?.[key], updateValue);
      return;
    }

    merged[key] = updateValue;
  });

  return merged;
}

// Builds initial runtime state from the schema's "state" section.
// First pass: copy normal values. Second pass: compute derived values.
export function buildInitialState(stateSpec = {}) {
  const state = {};

  Object.entries(stateSpec).forEach(([key, node]) => {
    if (node?.type !== 'computed') {
      state[key] = deepClone(node?.initialValue);
    }
  });

  return recomputeComputedState(stateSpec, state);
}

export function executeSchemaFunction(
  schema,
  functionName,
  currentState,
  args = {},
  event = {}
) {
  const stateSpec = schema?.state || {};
  const baseState = currentState ?? buildInitialState(stateSpec);
  const fnSpec = schema?.functions?.[functionName];

  if (!fnSpec) {
    return recomputeComputedState(stateSpec, baseState);
  }

  if (typeof fnSpec?.guard === 'string') {
    const allowed = Boolean(
      evaluateExpression(fnSpec.guard, {
        state: baseState,
        args,
        event
      })
    );

    if (!allowed) {
      return recomputeComputedState(stateSpec, baseState);
    }
  }

  const updates = resolveDynamicValues(fnSpec?.updatesState || {}, baseState, {
    args,
    event
  });

  return recomputeComputedState(stateSpec, mergeStateUpdates(baseState, updates, stateSpec));
}

export function resolveActionDefinition(actionDefinition, state, event = {}) {
  if (typeof actionDefinition === 'string') {
    return {
      functionName: actionDefinition,
      args: {}
    };
  }

  if (
    actionDefinition &&
    typeof actionDefinition === 'object' &&
    typeof actionDefinition.call === 'string'
  ) {
    return {
      functionName: actionDefinition.call,
      args: resolveDynamicValues(actionDefinition.args || {}, state, { event })
    };
  }

  return null;
}

// Builds the allowed component list from the UI component catalog JSON.
function getAllowedComponentTypes(componentsSchema) {
  const list = Array.isArray(componentsSchema?.components) ? componentsSchema.components : [];
  const fromSchema = list.map((item) => item?.name).filter(Boolean);
  const builtIn = [
    'Title',
    'Button',
    'Input',
    'Dropdown',
    'CheckBox',
    'Table',
    'FileUploadInput',
    'Card',
    'CardHeader',
    'CardTitle',
    'CardDescription',
    'CardContent',
    'CardFooter',
    'Tabs',
    'TabsList',
    'TabsTrigger',
    'TabsContent',
    'Grid.Item'
  ];

  return new Set(['Container', ...fromSchema, ...builtIn]);
}

function normalizeAvailableComponentSet(availableComponents) {
  if (!availableComponents) return null;

  if (availableComponents instanceof Set) {
    return new Set(Array.from(availableComponents).filter((item) => typeof item === 'string'));
  }

  if (Array.isArray(availableComponents)) {
    return new Set(availableComponents.filter((item) => typeof item === 'string'));
  }

  if (typeof availableComponents === 'object') {
    return new Set(Object.keys(availableComponents));
  }

  return null;
}

// Basic top-level validation using the general spec shape.
// We keep this lightweight on purpose for the renderer test.
function validateTopLevelSpec(schema, schemaShape) {
  if (!schema || typeof schema !== 'object') {
    throw new Error('Spec must be an object.');
  }

  const schemaProperties = schemaShape?.properties;
  const expectedSections = schemaProperties && typeof schemaProperties === 'object'
    ? Object.keys(schemaProperties)
    : Object.keys(schemaShape || {});
  const missing = expectedSections.filter((key) => schema[key] === undefined);
  if (missing.length > 0) {
    throw new Error(`Spec missing required top-level sections: ${missing.join(', ')}`);
  }
}

// Converts any schema node into a normalized shape:
// { type, props, children }
// It also checks that each node type is allowed.
function isActionDefinition(definition) {
  if (typeof definition === 'string') return true;
  if (!definition || typeof definition !== 'object') return false;
  return typeof definition.call === 'string';
}

function bindActionProps(action, props, bindAction) {
  if (typeof bindAction !== 'function') {
    return {};
  }

  const boundProps = {};

  if (action && typeof action === 'object') {
    Object.entries(action).forEach(([eventName, definition]) => {
      const handler = bindAction({ eventName, definition });
      if (typeof handler === 'function') {
        boundProps[eventName] = handler;
      }
    });
  }

  if (props && typeof props === 'object') {
    Object.entries(props).forEach(([propName, definition]) => {
      if (!propName.startsWith('on')) return;
      if (!isActionDefinition(definition)) return;

      const handler = bindAction({ eventName: propName, definition });
      if (typeof handler === 'function') {
        boundProps[propName] = handler;
      }
    });
  }

  return boundProps;
}

function resolveActionDynamicValues(value, state, contextOverrides = {}) {
  if (Array.isArray(value)) {
    return value.map((item) => resolveActionDynamicValues(item, state, contextOverrides));
  }

  if (value && typeof value === 'object') {
    const next = {};
    Object.entries(value).forEach(([key, inner]) => {
      next[key] = resolveActionDynamicValues(inner, state, contextOverrides);
    });
    return next;
  }

  if (typeof value === 'string' && value.startsWith('$event.')) {
    return value;
  }

  if (typeof value === 'string' && value.startsWith('$args.')) {
    return value;
  }

  if (typeof value === 'string' && value.startsWith('$')) {
    return evaluateExpression(value, {
      state,
      args: {},
      event: {},
      ...contextOverrides
    });
  }

  return value;
}

function normalizeNode(node, allowedTypes, state, bindAction, contextOverrides = {}) {
  if (node == null) return null;
  if (typeof node === 'number') return node;
  if (typeof node === 'string') {
    return node.startsWith('$')
      ? evaluateExpression(node, { state, ...(contextOverrides || {}) })
      : node;
  }

  if (node?.repeat && typeof node.repeat === 'object') {
    const sourceExpression =
      node.repeat.source ?? node.repeat.from ?? node.repeat.items ?? node.repeat.each;
    const source = evaluateExpression(sourceExpression, {
      state,
      args: {},
      event: {},
      ...(contextOverrides || {})
    });

    const list = Array.isArray(source) ? source : [];
    const itemName = typeof node.repeat.itemName === 'string' ? node.repeat.itemName : 'item';
    const indexName = typeof node.repeat.indexName === 'string' ? node.repeat.indexName : 'index';

    return list
      .map((item, index) => {
        const nextContext = {
          ...(contextOverrides || {}),
          vars: {
            ...((contextOverrides && contextOverrides.vars) || {}),
            [itemName]: item,
            [indexName]: index
          }
        };

        return normalizeNode(
          {
            ...node,
            repeat: undefined
          },
          allowedTypes,
          state,
          bindAction,
          nextContext
        );
      })
      .flat()
      .filter((child) => child !== null);
  }

  const type = node?.type;
  if (!type || !allowedTypes.has(type)) {
    throw new Error(`Unsupported component type: ${String(type)}`);
  }

  const normalizedChildren = [];
  const pushNormalized = (childNode) => {
    const normalized = normalizeNode(childNode, allowedTypes, state, bindAction, contextOverrides);
    if (normalized == null) return;
    if (Array.isArray(normalized)) {
      normalized.forEach((inner) => {
        if (inner != null) normalizedChildren.push(inner);
      });
      return;
    }
    // Plain objects can't be rendered as React children — serialize or skip empty ones
    if (typeof normalized === 'object' && !normalized.type) {
      const str = JSON.stringify(normalized);
      if (str && str !== '{}') normalizedChildren.push(str);
      return;
    }
    normalizedChildren.push(normalized);
  };

  if (Array.isArray(node.children)) {
    node.children.forEach(pushNormalized);
  } else if (node.children != null) {
    pushNormalized(node.children);
  }

  const resolvedNodeProps = resolveDynamicValues(node.props || {}, state, contextOverrides);

  // If the LLM placed a spec node (or array of spec nodes) inside props.children instead of the
  // top-level children field, move it into normalizedChildren so layoutrenderer never receives a
  // raw {type, props} object as a React child.
  const isSpecNode = (v) =>
    v && typeof v === 'object' && !Array.isArray(v) &&
    typeof v.type === 'string' && allowedTypes.has(v.type);

  if (resolvedNodeProps.children != null) {
    const rawPropChildren = resolvedNodeProps.children;
    if (isSpecNode(rawPropChildren)) {
      pushNormalized(rawPropChildren);
      delete resolvedNodeProps.children;
    } else if (Array.isArray(rawPropChildren) && rawPropChildren.some(isSpecNode)) {
      rawPropChildren.forEach((c) => pushNormalized(c));
      delete resolvedNodeProps.children;
    }
  }

  const resolvedNodeAction = resolveActionDynamicValues(node.action || {}, state, contextOverrides);

  const resolvedProps = {
    ...resolvedNodeProps,
    ...bindActionProps(resolvedNodeAction, resolvedNodeProps, bindAction)
  };

  if (resolvedProps.hidden === true) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(resolvedProps, 'hidden')) {
    delete resolvedProps.hidden;
  }

  return {
    type,
    props: resolvedProps,
    children: normalizedChildren
  };
}

// Public helper used by the route/page.
// Input: raw page JSON + optional catalogs.
// Output: normalized render tree.
export function buildSpecTree(
  schema,
  {
    uiComponents = uiComponentsSchema,
    specSchema: pageSpecSchema = specSchema,
    state,
    bindAction,
    availableComponents
  } = {}
) {
  validateTopLevelSpec(schema, pageSpecSchema);

  const schemaAllowedTypes = getAllowedComponentTypes(uiComponents);
  const runtimeAvailableTypes = normalizeAvailableComponentSet(availableComponents);
  const allowedTypes = runtimeAvailableTypes
    ? new Set(
        Array.from(schemaAllowedTypes).filter(
          (type) => type === 'Container' || runtimeAvailableTypes.has(type)
        )
      )
    : schemaAllowedTypes;

  const currentState = state ?? buildInitialState(schema?.state || {});

  return normalizeNode(schema?.return, allowedTypes, currentState, bindAction);
}

export default buildSpecTree;